import React, { useRef, useState } from 'react';
import { HttpStatusCode, ResponseType } from 'axios';

import { useSnackbar } from 'notistack';
import { Severity } from 'src/Api/Enum/Snackbar';
import { CustomSnackbar } from 'src/components/snackbar/CustomSnackbar';
import { apiRequest } from 'src/utils/apiRequest';

// Const object instead of enum so values are assignable to literal union types.
export const axiosMethod = {
  post: 'post',
  get: 'get',
  put: 'put',
  delete: 'delete',
} as const;
export type axiosMethod = (typeof axiosMethod)[keyof typeof axiosMethod];

type axiosProps = {
  url: string;
  method: axiosMethod;
  payload?: any;
  params?: any;
  headers?: any;
  responseType?: ResponseType;
};

/**
 * Optional snackbar notifications for useAxiosV2.
 * onSuccess: message shown on successful response (omit to disable).
 * onError: fixed string, or true to use the parsed error message automatically.
 */
export type SnackbarConfig = {
  onSuccess?: string;
  onError?: string | true;
};

/**
 * Hook for making authenticated API requests against the versioned business API.
 * Manages loading, error, success states and supports request cancellation.
 * Pass a SnackbarConfig to get automatic notistack notifications.
 */
export const useAxiosV2 = <T>(snackbar?: SnackbarConfig) => {
  const [data, setData] = useState<T | undefined>(undefined);
  const [responseHeaders, setResponseHeader] = useState<any>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const [loaded, setLoaded] = useState(true);
  const [success, setSuccess] = useState<boolean | undefined>(undefined);
  const controllerRef = useRef(new AbortController());

  // Always call useSnackbar — React rules of hooks forbid conditional calls.
  // The snackbar param controls whether we actually enqueue anything.
  const { enqueueSnackbar } = useSnackbar();

  const showSnackbar = (message: string, severity: Severity) => {
    enqueueSnackbar(message, {
      // React.createElement avoids JSX in a .ts file
      content: (key, msg) => React.createElement(CustomSnackbar, { id: key, message: msg, severity }),
    });
  };

  const cancel = () => controllerRef.current.abort();

  const axiosFetch = async ({ url, method, payload, params, headers, responseType }: axiosProps) => {
    setLoaded(false);
    setError(undefined);
    setSuccess(undefined);

    const result = await apiRequest<T>({
      url,
      method,
      body: payload,
      params,
      headers,
      responseType,
      signal: controllerRef.current.signal,
    });

    if (result.success) {
      setData(result.data);
      setResponseHeader(result.headers);
      setSuccess(true);
      if (snackbar?.onSuccess) showSnackbar(snackbar.onSuccess, Severity.SUCCESS);
    } else {
      setSuccess(false);
      // Network errors (no response) do not surface as UI error state
      if (result.status !== undefined) {
        setResponseHeader(result.headers);
        let message: string;
        if (result.status === HttpStatusCode.NotFound && !result.error) {
          setData(undefined);
          message = 'Not Found';
        } else {
          message = result.error ?? 'Error';
        }
        setError(message);
        if (snackbar?.onError) {
          showSnackbar(snackbar.onError === true ? message : snackbar.onError, Severity.ERROR);
        }
      }
    }

    setLoaded(true);
  };

  return { cancel, data, responseHeaders, error, loaded, success, setError, setLoaded, axiosFetch };
};